import { create } from 'zustand'
import { authApi, AuthApiError } from '@/lib/auth-api'
import { getCurrentPushToken } from '@/hooks/usePushNotifications'
import { notificationsApi } from '@/lib/notifications-api'
import { appLockService } from '@/lib/app-lock'
import { useAppLockStore } from '@/stores/app-lock.store'
import {
  identifyUser,
  clearUserIdentity,
  trackEvent,
  Events,
  setSuperProperties,
  clearSuperProperties
} from '@/lib/observability'
import type { User, LoginCredentials, RegisterCredentials } from '@/types/auth'

interface AuthState {
  user: User | null
  accessToken: string | null
  isLoading: boolean
  isInitialized: boolean
  error: string | null

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>
  register: (credentials: RegisterCredentials) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<boolean>
  initialize: () => Promise<void>
  clearError: () => void
  setUser: (user: User) => void
  resendVerification: () => Promise<{ verificationUrl?: string }>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  login: async (credentials) => {
    set({ isLoading: true, error: null })
    try {
      const response = await authApi.login(credentials)
      set({
        user: response.user,
        accessToken: response.accessToken,
        isLoading: false,
      })
      // Track login and identify user with properties
      identifyUser(response.user.id, {
        email: response.user.email,
        name: response.user.name || undefined,
        role: response.user.role,
        emailVerified: response.user.emailVerified,
      })
      // Set super properties so role is included in all events
      setSuperProperties({ user_role: response.user.role })
      trackEvent(Events.LOGIN, { role: response.user.role })
    } catch (error) {
      const message = error instanceof AuthApiError ? error.message : 'Login failed'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  register: async (credentials) => {
    set({ isLoading: true, error: null })
    trackEvent(Events.SIGNUP_STARTED, { role: credentials.role })
    try {
      const response = await authApi.register(credentials)
      set({
        user: response.user,
        accessToken: response.accessToken,
        isLoading: false,
      })
      // Track signup completion and identify user
      identifyUser(response.user.id, {
        email: response.user.email,
        name: response.user.name || undefined,
        role: response.user.role,
        emailVerified: response.user.emailVerified,
        signupDate: new Date().toISOString(),
      })
      // Set super properties so role is included in all events
      setSuperProperties({ user_role: response.user.role })
      trackEvent(Events.SIGNUP_COMPLETED, { role: response.user.role })
    } catch (error) {
      const message = error instanceof AuthApiError ? error.message : 'Registration failed'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  logout: async () => {
    const { accessToken } = get()
    trackEvent(Events.LOGOUT)
    try {
      // Unregister push token before logging out
      const pushToken = getCurrentPushToken()
      if (pushToken) {
        await notificationsApi.unregisterToken(pushToken).catch(() => {
          // Ignore errors - user is logging out anyway
        })
      }

      // Clear app lock settings
      await appLockService.clearAll().catch(() => {
        // Ignore errors
      })

      // Reset app lock store so it re-initializes for next user
      useAppLockStore.getState().reset()

      if (accessToken) {
        await authApi.logout(accessToken)
      }
    } finally {
      set({ user: null, accessToken: null })
      // Clear user identity and super properties from analytics
      clearSuperProperties(['user_role'])
      clearUserIdentity()
    }
  },

  refreshToken: async () => {
    try {
      const response = await authApi.refresh()
      set({
        user: response.user,
        accessToken: response.accessToken,
      })
      return true
    } catch {
      set({ user: null, accessToken: null })
      return false
    }
  },

  initialize: async () => {
    if (get().isInitialized) return

    set({ isLoading: true })
    try {
      // Try to refresh token on app start
      const response = await authApi.refresh()
      set({
        user: response.user,
        accessToken: response.accessToken,
        isInitialized: true,
        isLoading: false,
      })
      // Identify user for analytics (session restored)
      identifyUser(response.user.id, {
        email: response.user.email,
        name: response.user.name || undefined,
        role: response.user.role,
        emailVerified: response.user.emailVerified,
      })
      // Set super properties so role is included in all events
      setSuperProperties({ user_role: response.user.role })
    } catch {
      // No valid session, that's okay
      set({
        isInitialized: true,
        isLoading: false,
      })
    }
  },

  clearError: () => set({ error: null }),

  setUser: (user) => set({ user }),

  resendVerification: async () => {
    const { accessToken } = get()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await authApi.resendVerification(accessToken)
    return { verificationUrl: response.verificationUrl }
  },
}))
